import { Collapse, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import AuditDetailPanel from "~/components/AdminAudit/AuditDetailPanel";
import SmallButton from "~/components/Button/SmallButton";
import { ChipPredefined } from "~/components/Chip";
import { DateFormat } from "~/utils/Types";
import type { AdminAuditEntry } from "~/utils/adminAudit";
import dayjs from "~/utils/dayjs";

const LIST_MAX_HEIGHT = 260;

export interface AuditEntryListProps {
  entries: AdminAuditEntry[];
  total: number;
  isLoading: boolean;
  emptyLabel: string;
  onShowMore: () => void;
}

const AuditEntryList = ({
  entries,
  total,
  isLoading,
  emptyLabel,
  onShowMore,
}: AuditEntryListProps) => {
  const { t } = useTranslation("audit");
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);

  return (
    <Stack gap={4} mah={LIST_MAX_HEIGHT} style={{ overflowY: "auto" }}>
      {entries.length === 0 && !isLoading && (
        <Text size="sm" c="var(--mantine-color-text-tertiary)">
          {emptyLabel}
        </Text>
      )}
      {entries.map((entry) => (
        <Stack key={entry._id} gap={0}>
          <UnstyledButton
            onClick={() =>
              setExpandedEntryId((previous) =>
                previous === entry._id ? null : entry._id,
              )
            }
          >
            <Group gap={12} wrap="nowrap" align="center" py={4}>
              <Text size="xs" w={150} style={{ flexShrink: 0 }}>
                {dayjs(entry.at).format(DateFormat.DEFAULT)}
              </Text>
              <Text size="xs" style={{ flex: 1, minWidth: 0 }} truncate="end">
                {entry.actor.email ?? t("actor.unknown")}
              </Text>
              <Text size="xs" style={{ flex: 1, minWidth: 0 }} truncate="end">
                {entry.action}
              </Text>
              <Text size="xs" style={{ flex: 1, minWidth: 0 }} truncate="end">
                {entry.target.label ?? entry.target.id}
              </Text>
              <ChipPredefined
                type={entry.outcome === "refused" ? "error" : "success"}
                label={t(`outcomes.${entry.outcome}`)}
              />
            </Group>
          </UnstyledButton>
          <Collapse expanded={expandedEntryId === entry._id}>
            <AuditDetailPanel entry={entry} />
          </Collapse>
        </Stack>
      ))}
      {entries.length < total && (
        <Group>
          <SmallButton
            title={t("recentChanges.showMore")}
            variant="outlined"
            onClick={onShowMore}
          />
        </Group>
      )}
    </Stack>
  );
};

export default AuditEntryList;
